// @ts-nocheck
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Download, 
  Search, 
  FileImage, 
  FileText, 
  File,
  Loader2,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

const MarketingMaterials = () => {
  const { profile } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string | null>(null);

  // Get current affiliate ID
  const { data: affiliateData } = useQuery({
    queryKey: ['current-affiliate', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return null;
      const { data, error } = await supabase
        .from('affiliates')
        .select('id')
        .eq('user_id', profile.user_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!profile?.id,
  });

  // Fetch promo assets
  const { data: assets, isLoading } = useQuery({
    queryKey: ['affiliate-promo-assets', affiliateData?.id],
    queryFn: async () => {
      if (!affiliateData?.id) return [];

      const { data, error } = await supabase
        .from('affiliate_promo_assets')
        .select('*')
        .or(`assigned_to_all.eq.true,assigned_affiliate_ids.cs.{${affiliateData.id}}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!affiliateData?.id,
  });

  // Get unique display types for filtering
  const displayTypes = React.useMemo(() => {
    if (!assets) return [];
    const types = new Set(assets.map(a => a.display_type).filter(Boolean));
    return Array.from(types);
  }, [assets]);

  // Filter assets based on search and type
  const filteredAssets = React.useMemo(() => {
    if (!assets) return [];
    
    return assets.filter(asset => {
      const matchesSearch = !searchTerm || 
        asset.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = !selectedType || asset.display_type === selectedType;
      
      return matchesSearch && matchesType;
    });
  }, [assets, searchTerm, selectedType]);

  // Format file size
  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get file icon based on mime type
  const getFileIcon = (mimeType: string | null) => {
    if (!mimeType) return <File className="w-8 h-8 text-muted-foreground" />;
    
    if (mimeType.startsWith('image/')) {
      return <FileImage className="w-8 h-8 text-primary" />;
    }
    if (mimeType.includes('pdf')) {
      return <FileText className="w-8 h-8 text-red-500" />;
    }
    return <File className="w-8 h-8 text-muted-foreground" />;
  };

  // Handle download with tracking
  const handleDownload = async (asset: any) => {
    if (!affiliateData?.id) return;

    try {
      // Get UTM parameters from URL
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      const utmMedium = urlParams.get('utm_medium');
      const utmCampaign = urlParams.get('utm_campaign');

      // Track the click
      const { error: trackError } = await supabase
        .from('affiliate_asset_clicks')
        .insert({
          affiliate_id: affiliateData.id,
          asset_id: asset.id,
          clicked_at: new Date().toISOString(),
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
        });

      if (trackError) {
        console.error('Error tracking click:', trackError);
      }

      // Open download in new tab
      window.open(asset.file_url, '_blank');
      toast.success('Download started');
    } catch (error) {
      console.error('Error handling download:', error);
      toast.error('Failed to download asset');
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-primary-700">Marketing Materials</h1>
          <p className="text-muted-foreground">Loading promotional assets...</p>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-primary-700">Marketing Materials</h1>
        <p className="text-muted-foreground">
          Download promotional assets to help you promote our products
        </p>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search materials by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {displayTypes.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={selectedType === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedType(null)}
            >
              All ({assets?.length || 0})
            </Button>
            {displayTypes.map((type) => (
              <Button
                key={type}
                variant={selectedType === type ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedType(type)}
              >
                {type}
              </Button>
            ))}
          </div>
        )}
      </div>

      {/* Results Count */}
      {filteredAssets && filteredAssets.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Showing {filteredAssets.length} {filteredAssets.length === 1 ? 'material' : 'materials'}
        </p>
      )}

      {/* Asset Grid */}
      {filteredAssets && filteredAssets.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => (
            <Card key={asset.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardContent className="p-0">
                {/* Preview/Icon */}
                <div className="bg-muted/50 h-48 flex items-center justify-center relative">
                  {asset.mime_type?.startsWith('image/') ? (
                    <img
                      src={asset.file_url}
                      alt={asset.name}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.parentElement?.appendChild(
                          document.createElement('div')
                        );
                      }}
                    />
                  ) : (
                    getFileIcon(asset.mime_type)
                  )}
                  
                  {/* Display Type Badge */}
                  {asset.display_type && (
                    <Badge className="absolute top-2 right-2">
                      {asset.display_type}
                    </Badge>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold text-lg line-clamp-1">
                      {asset.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2">
                      {asset.mime_type && (
                        <Badge variant="secondary" className="text-xs">
                          {asset.mime_type.split('/')[1]?.toUpperCase() || 'FILE'}
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatFileSize(asset.file_size)}
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleDownload(asset)}
                    className="w-full"
                    size="sm"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        // Empty State
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <div className="p-4 rounded-full bg-muted">
              <Package className="w-12 h-12 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm || selectedType 
                  ? 'No materials match your search' 
                  : 'No marketing materials available yet'}
              </h3>
              <p className="text-muted-foreground">
                {searchTerm || selectedType
                  ? 'Try adjusting your search or filters'
                  : 'Check back soon! Marketing materials will be added here.'}
              </p>
            </div>
            {(searchTerm || selectedType) && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedType(null);
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default MarketingMaterials;
